import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Box, Text } from "@primer/react";
import { AccessAvatar } from "./AccessAvatar";
import type { Grantee } from "./types";

const MAX_VISIBLE_AVATARS = 3;
const AVATAR_SIZE = 22;

interface AccessTriggerPillProps {
  grantees: Grantee[];
  label: string;
  onClick: () => void;
  expanded: boolean;
}

// Pill-style trigger: stacked avatars (overlap by 8px), overflow count,
// vertical divider, contextual label. Uses AccessAvatar directly to
// avoid Primer Avatar's SVG-src sizing quirk.
export const AccessTriggerPill = forwardRef<HTMLButtonElement, AccessTriggerPillProps>(
  function AccessTriggerPill({ grantees, label, onClick, expanded }, ref) {
    const visible = grantees.slice(0, MAX_VISIBLE_AVATARS);
    const overflow = Math.max(grantees.length - visible.length, 0);
    const innerRef = useRef<HTMLButtonElement | null>(null);
    useImperativeHandle(ref, () => innerRef.current as HTMLButtonElement);

    // Primer's BaseStyles (mounted via components/PrimerRoot.tsx) wraps our
    // tree in a div with a hardcoded canvas.default (white) background.
    // Walk up to that wrapper and force it transparent so the pill blends
    // with whatever surface it sits on.
    useEffect(() => {
      const baseStyles = innerRef.current?.closest<HTMLElement>("[data-portal-root='true']");
      if (baseStyles) baseStyles.style.backgroundColor = "transparent";
    }, []);

    return (
      <Box
        as="button"
        ref={innerRef}
        type="button"
        onClick={onClick}
        aria-haspopup="dialog"
        aria-expanded={expanded}
        aria-label={label}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          height: "40px",
          paddingY: 0,
          paddingLeft: visible.length > 0 ? "10px" : "16px",
          paddingRight: "16px",
          backgroundColor: "canvas.default",
          border: "1px solid",
          borderColor: "border.default",
          // Asymmetric corners — full pill cap on the left (height/2 = 20px),
          // button-like 6px on the right to match the adjacent large Button.
          // Don't use a huge value like 999px here: the spec scales ALL corners
          // by min(side/sum-of-radii) when any side overflows, which silently
          // crushes the right corners to ~0.
          // Order: top-left, top-right, bottom-right, bottom-left.
          borderRadius: "20px 6px 6px 20px",
          boxShadow: "shadow.small",
          cursor: "pointer",
          fontFamily: "inherit",
          fontSize: 1,
          fontWeight: "semibold",
          color: "fg.default",
          "&:hover": { backgroundColor: "canvas.subtle", borderColor: "border.muted" },
          "&:focus-visible": { outline: "2px solid", outlineColor: "accent.emphasis" },
        }}
      >
        {visible.length > 0 && (
          <Box sx={{ display: "flex", paddingLeft: "4px" }}>
            {visible.map((g, idx) => (
              <Box
                key={g.id}
                sx={{ marginLeft: idx === 0 ? 0 : "-8px" }}
              >
                <AccessAvatar src={g.avatarUrl} alt={g.name} size={AVATAR_SIZE} ringed />
              </Box>
            ))}
          </Box>
        )}
        {overflow > 0 && (
          <Text sx={{ color: "fg.muted", fontWeight: "normal", fontSize: 1 }}>+{overflow}</Text>
        )}
        {visible.length > 0 && (
          <Box sx={{ borderLeft: "1px solid", borderColor: "border.default", height: "16px", marginX: "4px" }} />
        )}
        <Text>{label}</Text>
      </Box>
    );
  },
);
